/**
 * ScheduleTaskClient — REST client for CMHKScheduleTaskService with SigV4 auth.
 *
 * Auth flow (same as SkillClient):
 *   Federate id_token → Cognito Identity Pool → IAM temp credentials → SigV4
 *
 * Credentials are cached and reused until they expire (checked via expiration field).
 *
 * Usage:
 *   import { scheduleTaskClient } from '../client/ScheduleTaskClient';
 *   const tasks = await scheduleTaskClient.listTasks({ owner: 'alias' });
 */

import { SignatureV4 } from '@smithy/signature-v4';
import { HttpRequest } from '@smithy/protocol-http';
import { Sha256 } from '@aws-crypto/sha256-browser';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import type { AwsCredentialIdentity } from '@smithy/types';
import { appConfig } from '../config';
import { getIdToken, refreshAuth } from '../context/AuthContext';

// ── Types (mirroring Smithy model) ──────────────────────────────────────────

export type ScopeType = 'personal' | 'org' | 'global';
export type TaskStatus = 'SCHEDULED' | 'COMPLETED' | 'INVOKE_FAILED';
export type ExecutionStatus = 'SUCCESS' | 'FAILED' | 'RUNNING';

export interface TaskDetail {
  taskId: string;
  scopeType: ScopeType;
  name: string;
  scopeId: string;
  scheduleExpression: string;
  creator: string;
  agent?: string;
  payload: string;
  status: TaskStatus;
  createdAt: string;
  lastExecutedAt: string | null;
}

export interface ExecutionRecord {
  taskId: string;
  content: string;
  contentType: string;
  durationMs: number;
  executedAt: string;
  status: ExecutionStatus;
  executor: string;
}

export interface ListTasksParams { owner: string; agent?: string; }
export interface ListTasksResponse { tasks: TaskDetail[]; }

export interface ListExecutionHistoryParams {
  taskId: string;
  startTime?: string;
  endTime?: string;
  status?: ExecutionStatus;
  contentType?: string;
}
export interface ListExecutionHistoryResponse { executions: ExecutionRecord[]; }

export interface ListExecutionsParams {
  executor: string;
  startTime?: string;
  endTime?: string;
  status?: ExecutionStatus;
  contentType?: string;
  agent?: string;
}
export interface ListExecutionsResponse { executions: ExecutionRecord[]; }

// ── Error ───────────────────────────────────────────────────────────────────

export class ScheduleTaskApiError extends Error {
  constructor(
    public readonly statusCode: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ScheduleTaskApiError';
  }
}

// ── Cached credential provider ──────────────────────────────────────────────

const EXPIRY_BUFFER_MS = 5 * 60 * 1000; // refresh 5 min before expiry

type CachedCredentialsProvider = (() => Promise<AwsCredentialIdentity>) & { invalidate: () => void };

function createCachedCredentialsProvider(): CachedCredentialsProvider {
  const rawProvider = fromCognitoIdentityPool({
    identityPoolId: appConfig.identityPoolId,
    logins: {
      [appConfig.identityProvider]: () => {
        const token = getIdToken();
        if (!token) throw new Error('No id_token available');
        return Promise.resolve(token);
      },
    },
    clientConfig: { region: appConfig.region },
  });

  let cached: AwsCredentialIdentity | null = null;
  let inflightPromise: Promise<AwsCredentialIdentity> | null = null;

  const provider = (async (): Promise<AwsCredentialIdentity> => {
    // Return cached if still valid
    if (cached?.expiration && cached.expiration.getTime() - Date.now() > EXPIRY_BUFFER_MS) {
      return cached;
    }

    // Deduplicate concurrent calls — only one inflight Cognito request at a time
    if (inflightPromise) return inflightPromise;

    inflightPromise = rawProvider().then((creds) => {
      cached = creds;
      inflightPromise = null;
      return creds;
    }).catch((err) => {
      inflightPromise = null;
      cached = null;
      throw err;
    });

    return inflightPromise;
  }) as CachedCredentialsProvider;

  // Drop the cached credentials so the next call re-derives them from Cognito.
  // Used after an auth failure (403/401), once the underlying Federate id_token
  // has been refreshed, so the stale temporary credentials are not reused.
  provider.invalidate = () => {
    cached = null;
    inflightPromise = null;
  };

  return provider;
}

// ── Client ──────────────────────────────────────────────────────────────────

class ScheduleTaskClient {
  private credentials = createCachedCredentialsProvider();

  private get baseUrl(): string {
    return appConfig.scheduleTaskApiEndpoint;
  }

  private async signedRequest<T>(
    method: string,
    path: string,
    queryParams?: Record<string, string>,
    body?: string,
  ): Promise<T> {
    const url = new URL(`${this.baseUrl}${path}`);
    if (queryParams) {
      Object.entries(queryParams).forEach(([k, v]) => {
        if (v !== undefined && v !== '') url.searchParams.set(k, v);
      });
    }

    const doSignedFetch = async (): Promise<Response> => {
      const headers: Record<string, string> = { host: url.hostname };
      if (body) headers['content-type'] = 'application/json';
      // Application-layer identity: forward the verified Federate id_token so the
      // service derives the caller from the token `sub` (VERIFIED) instead of the
      // request-body identity. SigV4 (transport) and X-Id-Token (application) run
      // side by side. Added before signing so it is part of the SignedHeaders.
      // If no id_token is available the header is omitted — the service then falls
      // back to body identity (ABSENT) during the parallel-rollout phase.
      const idToken = getIdToken();
      if (idToken) headers['x-id-token'] = idToken;

      const httpRequest = new HttpRequest({
        method,
        protocol: url.protocol,
        hostname: url.hostname,
        port: url.port ? Number(url.port) : undefined,
        path: url.pathname,
        query: Object.fromEntries(url.searchParams.entries()),
        headers,
        body: body ?? undefined,
      });

      const signer = new SignatureV4({
        service: 'execute-api',
        region: appConfig.region,
        credentials: this.credentials,
        sha256: Sha256,
      });

      const signed = await signer.sign(httpRequest);

      const fetchUrl = `${url.origin}${signed.path}${url.search}`;
      return fetch(fetchUrl, {
        method: signed.method,
        headers: signed.headers as Record<string, string>,
        body: signed.body as string | undefined,
      });
    };

    let response = await doSignedFetch();

    // 401 (X-Id-Token rejected) or 403 (expired SigV4 temporary credentials, seen
    // as UnrecognizedClientException) both mean our auth material is stale. Both
    // stem from an expired Federate id_token: refresh it, drop the cached Cognito
    // credentials so they are re-derived from the new token, then retry once.
    // In the browser these failures surface as opaque "CORS" errors because API
    // Gateway's IAM-layer 403 response may omit CORS headers — the real cause is
    // the stale token, not CORS.
    if (response.status === 401 || response.status === 403) {
      const newToken = await refreshAuth();
      if (newToken) {
        this.credentials.invalidate();
        response = await doSignedFetch();
      }
      // Still failing after refresh (or refreshAuth triggered a login redirect) → bail.
    }

    if (!response.ok) {
      let errorBody: { message?: string; code?: string };
      try { errorBody = await response.json(); } catch { errorBody = {}; }
      throw new ScheduleTaskApiError(
        response.status,
        errorBody.code ?? 'unknown',
        errorBody.message ?? `HTTP ${response.status}`,
      );
    }

    return response.json();
  }

  // ── API Methods ─────────────────────────────────────────────────────────

  async listTasks(params: ListTasksParams): Promise<ListTasksResponse> {
    const query: Record<string, string> = { owner: params.owner };
    if (params.agent) query.agent = params.agent;
    return this.signedRequest<ListTasksResponse>('GET', '/tasks', query);
  }

  async listExecutionHistory(params: ListExecutionHistoryParams): Promise<ListExecutionHistoryResponse> {
    const query: Record<string, string> = {};
    if (params.startTime) query.startTime = params.startTime;
    if (params.endTime) query.endTime = params.endTime;
    if (params.status) query.status = params.status;
    if (params.contentType) query.contentType = params.contentType;
    return this.signedRequest<ListExecutionHistoryResponse>(
      'GET', `/tasks/${encodeURIComponent(params.taskId)}/executions`, query,
    );
  }

  /** GET /executions?executor=...&startTime=...&status=...&contentType=... */
  async listExecutions(params: ListExecutionsParams): Promise<ListExecutionsResponse> {
    const query: Record<string, string> = { executor: params.executor };
    if (params.startTime) query.startTime = params.startTime;
    if (params.endTime) query.endTime = params.endTime;
    if (params.status) query.status = params.status;
    if (params.contentType) query.contentType = params.contentType;
    if (params.agent) query.agent = params.agent;
    return this.signedRequest<ListExecutionsResponse>('GET', '/executions', query);
  }

  /** Reset credentials (e.g. after re-login). */
  resetCredentials(): void {
    this.credentials = createCachedCredentialsProvider();
  }
}

// ── Singleton ───────────────────────────────────────────────────────────────

export const scheduleTaskClient = new ScheduleTaskClient();
